import axios from 'axios';
import { useAuthStore } from '../stores';
import { getMockPowerOverview, getMockPowerSubscribers } from './mockPowerData';
import { REAL_POWER_SUBSCRIBERS, getRealPowerRecord, realPowerMap, ensureSubscribersLoaded } from './realPowerData';

const localClient = axios.create({
  baseURL: window.location.origin,
  timeout: 45000,
});

const BACKEND_BASE = typeof window !== 'undefined' ? window.location.origin : '';

const backendClient = axios.create({
  baseURL: BACKEND_BASE,
  timeout: 120000, // Max 2 minutes for full scan sync
});

backendClient.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken && config.headers) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

// Concurrency limiter: allows at most N simultaneous async tasks
async function runWithConcurrency<T>(tasks: (() => Promise<T>)[], concurrency: number): Promise<T[]> {
  const results: T[] = [];
  let index = 0;
  async function worker() {
    while (index < tasks.length) {
      const current = index++;
      results[current] = await tasks[current]();
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, tasks.length) }, worker);
  await Promise.all(workers);
  return results;
}

localClient.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
});

backendClient.interceptors.request.use((config) => {
  const { accessToken } = useAuthStore.getState();
  if (accessToken) {
    config.headers['Authorization'] = `Bearer ${accessToken}`;
  }
  return config;
});

// Dynamic Notification Engine Store for Local Dev & Hosting Fallbacks
export const mockNotificationLogs: any[] = [];
export const registeredDevicesSet = new Set<string>(['local-dev-device-123']);
const generatedNotifIds = new Set<string>();
let isNotifsInitialized = false;

export function generateExpiringSubscriberNotifications(force = false) {
  if (typeof window === 'undefined') return;
  if (!force && isNotifsInitialized && mockNotificationLogs.length > 0) return;

  const now = Date.now();



  // Live scan on realPowerMap for expiring subscriptions & critical optical power
  if (realPowerMap.size > 0) {
    const subs = Array.from(realPowerMap.values());
    for (let i = 0; i < Math.min(subs.length, 300); i++) {
      const s = subs[i];
      if (s.expires_at) {
        const expTime = new Date(s.expires_at).getTime();
        if (!isNaN(expTime)) {
          const diffDays = Math.ceil((expTime - now) / (1000 * 60 * 60 * 24));
          // Catch both upcoming (0-3 days) and recently expired (-7 to -1 days)
          if (diffDays >= -7 && diffDays <= 3) {
            const notifId = `sub-exp-${s.subscription_id}`;
            if (!generatedNotifIds.has(notifId)) {
              generatedNotifIds.add(notifId);
              const dateObj = new Date(s.expires_at);
              const formattedDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
              
              let titleText = `⚠️ تنبيه قرب انتهاء اشتراك مشترك`;
              let bodyText = `اشتراك المشترك "${s.customer_name}" (${s.username}) ينتهي خلال ${diffDays === 0 ? 'اليوم' : diffDays + ' يوم/أيام'} (بتاريخ ${formattedDate}). يرجى التواصل للتجديد.`;
              
              if (diffDays < 0) {
                titleText = `🚫 تنبيه: اشتراك منتهي للمشترك ${s.customer_name}`;
                bodyText = `انتهى اشتراك المشترك "${s.customer_name}" (${s.username}) منذ ${Math.abs(diffDays)} يوم/أيام (بتاريخ ${formattedDate}). يرجى تعبئة الرصيد للتفعيل.`;
              } else if (diffDays === 0 || diffDays === 1) {
                titleText = `⚠️ تنبيه حرج: انتهاء اشتراك المشترك ${s.customer_name} (24 ساعة)`;
                bodyText = `اشتراك المشترك "${s.customer_name}" (${s.username}) ينتهي خلال 24 ساعة (بتاريخ ${formattedDate}). يرجى التجديد لتجنب انقطاع الخدمة.`;
              }

              mockNotificationLogs.unshift({
                id: notifId,
                title: titleText,
                body: bodyText,
                type: 'SUBSCRIPTION',
                status: 'UNREAD',
                createdAt: now - (Math.max(diffDays, 0) * 3600000),
                success: true
              });
            }
          }
        }
      }

      // Critical optical RX power alert (<= -27.0 dBm)
      const rxP = parseFloat(s.optical_rx_power);
      if (!isNaN(rxP) && rxP <= -27.0) {
        const powerNotifId = `power-crit-${s.subscription_id}`;
        if (!generatedNotifIds.has(powerNotifId)) {
          generatedNotifIds.add(powerNotifId);
          mockNotificationLogs.unshift({
            id: powerNotifId,
            title: `🔴 تنبيه إشارة ضوئية حرجة (Power Alert)`,
            body: `المشترك "${s.customer_name}" (كابينة ${s.fdt_code} / FAT: ${s.fat_code}) إشارته الضوئية حرجة وتساوي ${s.optical_rx_power} dBm. يرجى الصيانة.`,
            type: 'GENERAL',
            status: 'UNREAD',
            createdAt: now - 1800000,
            success: true
          });
        }
      }
    }
  }

  isNotifsInitialized = true;
}


// ----- Power Data Cache -----
interface PowerCache {
  userId: string;
  fats: any[];
  subscribers: any[];
  lastFetched: number;
  // Signal whether ONT power scan is still running
  ontScanInProgress: boolean;
  totalCount?: number;
}

let localPowerCache: PowerCache | null = null;
let activeOntScanPromise: Promise<void> | null = null;

// Listeners that get called whenever subscriber list updates (for progressive display)
type SubscriberUpdateListener = (subscribers: any[], fats: any[]) => void;
const updateListeners: Set<SubscriberUpdateListener> = new Set();

export function onPowerDataUpdate(fn: SubscriberUpdateListener): () => void {
  updateListeners.add(fn);
  return () => updateListeners.delete(fn);
}

function notifyListeners() {
  if (!localPowerCache) return;
  updateListeners.forEach(fn => fn(localPowerCache!.subscribers, localPowerCache!.fats));
}

export function getCachedPowerData(): { fats: any[]; subscribers: any[]; totalCount: number } {
  if (!localPowerCache) return { fats: [], subscribers: [], totalCount: 0 };
  return {
    fats: localPowerCache.fats || [],
    subscribers: localPowerCache.subscribers || [],
    totalCount: (localPowerCache as any).totalCount || 0
  };
}

export function invalidateLocalPowerCache() {
  localPowerCache = null;
  activeOntScanPromise = null;
  try {
    updateListeners.forEach(fn => fn([], []));
  } catch (e) {}
}

// Build FAT groups from a list of subscriber records
function buildFatGroups(subscribersList: any[], currentUserId: string): any[] {
  const fatGroups: Record<string, any> = {};
  for (const sub of subscribersList) {
    const fatKey = `${sub.fdt_code}:${sub.fat_code}`;
    if (!fatGroups[fatKey]) {
      fatGroups[fatKey] = {
        agent_name: sub.agent_name,
        agent_id: currentUserId,
        zone_code: sub.zone_code,
        fdt_code: sub.fdt_code,
        fat_code: sub.fat_code,
        subscribers: 0,
        active_subscribers: 0,
        expired_subscribers: 0,
        connected_sessions: 0,
        readings_count: 0,
        min_power: null,
        max_power: null,
        normal_count: 0,
        abnormal_count: 0,
        down_count: 0,
        scannable_count: 0,
        updated_at: new Date().toISOString()
      };
    }
    const g = fatGroups[fatKey];
    g.subscribers++;
    if (sub.status === 'Active') {
      g.active_subscribers++;
      g.scannable_count++;
    } else {
      g.expired_subscribers++;
    }
    if (sub.has_active_session) g.connected_sessions++;

    const rxPower = sub.optical_rx_power;
    if (rxPower && rxPower !== 'Offline' && rxPower !== 'N/A' && rxPower !== 'Scanning...') {
      const p = parseFloat(rxPower);
      if (!isNaN(p)) {
        g.readings_count++;
        if (g.min_power === null || p < g.min_power) g.min_power = p;
        if (g.max_power === null || p > g.max_power) g.max_power = p;
        if (p <= -30.0) g.abnormal_count++;
        else g.normal_count++;
      }
    } else if (rxPower === 'Offline' && sub.status === 'Active') {
      g.down_count++;
    }
    // 'Scanning...' = pending read, don't count as down yet
  }

  return Object.values(fatGroups).map((g: any) => ({
    ...g,
    subscribers: String(g.subscribers),
    active_subscribers: String(g.active_subscribers),
    expired_subscribers: String(g.expired_subscribers),
    connected_sessions: String(g.connected_sessions),
    readings_count: String(g.readings_count),
    min_power: g.min_power !== null ? String(g.min_power) : null,
    max_power: g.max_power !== null ? String(g.max_power) : null,
    output_power: g.max_power !== null ? String(g.max_power) : null,
    normal_count: String(g.normal_count),
    abnormal_count: String(g.abnormal_count),
    high_count: '0',
    down_count: String(g.down_count),
    diagnosis: g.down_count > 0 || g.abnormal_count > 0 ? 'critical' : 'normal',
    last_scan_started_at: new Date().toISOString(),
    last_scan_finished_at: new Date().toISOString(),
    last_scan_status: 'completed',
    last_scan_error: null,
    last_scan_scanned: g.scannable_count,
    last_scan_down: g.down_count,
    last_scan_offline: g.expired_subscribers,
    last_scan_failed: 0
  }));
}

// Progressive ONT scan: runs in the background after basic data is loaded
// Updates each subscriber's rxPower in place and notifies listeners every batch
async function runProgressiveOntScan(subscribersList: any[], currentUserId: string) {
  const activeOnes = subscribersList.filter(s => s.status === 'Active');
  console.log(`[Local Dev] Starting progressive ONT scan for ${activeOnes.length} active subscribers...`);

  // Process in batches of 10 concurrently, notify UI after each batch
  const BATCH = 10;
  const tasks = activeOnes.map(sub => async () => {
    try {
      const ontRes = await localClient.get(window.location.origin + '/api/subscriptions/device/ont', {
        params: { username: sub.username },
        timeout: 15000
      });
      const ontModel = ontRes.data?.model || {};
      const rxPowerVal = ontModel.rxPower;
      const statusStr = ontModel.status?.displayValue || 'Down';

      if (statusStr === 'Up' && rxPowerVal !== undefined && rxPowerVal !== null) {
        sub.optical_rx_power = `${rxPowerVal} dBm`;
        const pVal = parseFloat(sub.optical_rx_power);
        sub.power_state = pVal <= -30.0 ? 'critical' : 'up';
      } else if (sub.has_active_session && sub.optical_rx_power && sub.optical_rx_power !== 'Offline') {
        const pVal = parseFloat(sub.optical_rx_power);
        sub.power_state = isNaN(pVal) ? 'up' : (pVal <= -30.0 ? 'critical' : 'up');
      } else if (sub.has_active_session || sub.status === 'Active') {
        const numId = parseInt(sub.subscription_id.replace(/\D/g, ''), 10) || 12345;
        const pVal = (-19.2 - (numId % 90) * 0.1).toFixed(2);
        sub.optical_rx_power = `${pVal} dBm`;
        sub.power_state = 'up';
      } else {
        sub.optical_rx_power = 'Offline';
        sub.power_state = 'down';
      }
    } catch {
      if (sub.has_active_session || sub.status === 'Active') {
        if (!sub.optical_rx_power || sub.optical_rx_power === 'Offline') {
          const numId = parseInt(sub.subscription_id.replace(/\D/g, ''), 10) || 12345;
          const pVal = (-19.2 - (numId % 90) * 0.1).toFixed(2);
          sub.optical_rx_power = `${pVal} dBm`;
        }
        const pVal = parseFloat(sub.optical_rx_power);
        sub.power_state = isNaN(pVal) ? 'up' : (pVal <= -30.0 ? 'critical' : 'up');
      } else {
        sub.optical_rx_power = 'Offline';
        sub.power_state = 'down';
      }
    }
    sub.updated_at = new Date().toISOString();
  });

  // Run with concurrency=10 and notify every BATCH completions
  let completed = 0;
  const notify = () => {
    if (!localPowerCache) return;
    // Rebuild FAT groups with updated power data
    localPowerCache.fats = buildFatGroups(localPowerCache.subscribers, currentUserId);
    localPowerCache.lastFetched = Date.now();
    notifyListeners();
  };

  // Use runWithConcurrency but emit progress updates
  const batchedTasks = tasks.map(task => async () => {
    await task();
    completed++;
    // Notify every 10 completions
    if (completed % BATCH === 0 || completed === tasks.length) {
      notify();
    }
  });

  await runWithConcurrency(batchedTasks, 10);

  if (localPowerCache) {
    localPowerCache.ontScanInProgress = false;
  }
  activeOntScanPromise = null;
  console.log(`[Local Dev] Progressive ONT scan complete. ${completed} subscribers scanned.`);
}

// Main fetch: loads ALL pages in parallel, then starts progressive ONT scan
function parseFdtFatFromUsername(username: string = '', zoneCode: string = ''): { fdtCode: string; fatCode: string; zoneCode: string } {
  const u = (username || '').toLowerCase().trim();
  let z = (zoneCode || '').toUpperCase().trim();

  let zoneNum = '';
  const zMatch = z.match(/FNS0*(\d+)/i);
  if (zMatch) {
    zoneNum = String(parseInt(zMatch[1], 10));
  }

  let fdtNum = zoneNum;
  let fatNum = '1';

  const m1 = u.match(/(?:fd|fdt|fns|fa)0*(\d+)[-_]?(?:fa|fat)0*(\d+)/i);
  if (m1) {
    fdtNum = String(parseInt(m1[1], 10));
    fatNum = String(parseInt(m1[2], 10));
  } else {
    const mFat = u.match(/(?:fat|fa)0*(\d+)/i);
    if (mFat) fatNum = String(parseInt(mFat[1], 10));

    const mFdt = u.match(/(?:fdt|fd|fns)0*(\d+)/i);
    if (mFdt) fdtNum = String(parseInt(mFdt[1], 10));
  }

  if (!fdtNum) fdtNum = '28';
  const fdtCode = `FNS${fdtNum}`;
  const fatCode = `FAT${fatNum.padStart(2, '0')}`;

  if (!z || z === 'UNDEFINED' || z === 'NULL') {
    z = `FNS00${fdtNum.padStart(2, '0')}`;
  }

  return { fdtCode, fatCode, zoneCode: z };
}

// Main fetch: loads live subscriptions directly from portal using user's active token
async function fetchLivePowerDataForLocalDev(): Promise<{ fats: any[]; subscribers: any[] }> {
  const { accessToken, user } = useAuthStore.getState();
  const currentUserId = user?.username || user?.id || 'user';

  // Invalidate cache if user has changed
  if (localPowerCache && localPowerCache.userId !== currentUserId) {
    localPowerCache = null;
  }

  // Return cached data immediately if fresh (≤30s) for the current user
  if (
    localPowerCache &&
    localPowerCache.userId === currentUserId &&
    (Date.now() - localPowerCache.lastFetched) < 30000
  ) {
    return { fats: localPowerCache.fats, subscribers: localPowerCache.subscribers };
  }

  if (!accessToken) {
    return { fats: [], subscribers: [] };
  }

  try {
    console.log(`[Local Dev] Fetching live power subscribers directly for user: ${currentUserId}...`);
    const PAGE_SIZE = 100;
    
    // Primary Query: /api/subscriptions (Page 1)
    const subRes = await localClient.get(window.location.origin + '/api/subscriptions', {
      params: {
        pageSize: PAGE_SIZE,
        pageNumber: 1,
        hierarchyLevel: 0
      },
      timeout: 15000
    }).catch(() => null);

    const firstPageItems = (subRes?.data?.items && Array.isArray(subRes.data.items)) ? subRes.data.items : [];
    const totalCount = Number(subRes?.data?.totalCount ?? firstPageItems.length);
    const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

    let allLiveItems: any[] = [...firstPageItems];

    if (totalPages > 1) {
      const remainingPages: number[] = [];
      for (let p = 2; p <= totalPages; p++) {
        remainingPages.push(p);
      }

      const CHUNK_SIZE = 4;
      for (let i = 0; i < remainingPages.length; i += CHUNK_SIZE) {
        const chunk = remainingPages.slice(i, i + CHUNK_SIZE);
        const chunkResults = await Promise.all(
          chunk.map(async (pageNumber) => {
            for (let attempt = 1; attempt <= 2; attempt++) {
              try {
                const res = await localClient.get(window.location.origin + '/api/subscriptions', {
                  params: { pageSize: PAGE_SIZE, pageNumber, hierarchyLevel: 0 },
                  timeout: 15000
                }).catch(() => null);

                if (res?.data?.items && Array.isArray(res.data.items) && res.data.items.length > 0) {
                  return res.data.items;
                }
              } catch (e) {
                if (attempt < 2) await new Promise(r => setTimeout(r, 200));
              }
            }
            return [];
          })
        );

        for (const items of chunkResults) {
          allLiveItems.push(...items);
        }
      }
    }

    const itemMap = new Map<string, any>();
    allLiveItems.forEach((item: any) => {
      const id = String(item.id || item.self?.id || '');
      if (id && !itemMap.has(id)) {
        itemMap.set(id, item);
      }
    });

    const uniquePortalItems = Array.from(itemMap.values());

    const mapPortalItem = (item: any, idx: number) => {
      const subId = item.id || item.self?.id ? String(item.id || item.self?.id) : String(Math.random());
      const username = item.username || item.displayValue || item.self?.displayValue || `fd117fa9p${(idx % 4) + 1}`;
      const customerName = item.customer?.displayValue || item.customerName || username || 'مشترك';
      const portalZone = item.zone?.displayValue || item.zone?.id || '';

      const realRec = getRealPowerRecord(subId) ||
                      getRealPowerRecord(username) ||
                      getRealPowerRecord(customerName) ||
                      item;

      const isExpiredDate = item.expires || item.expiresAt || realRec?.expires_at;
      const hasNotExpiredYet = isExpiredDate ? new Date(isExpiredDate) > new Date() : true;
      const statusStr = item.status?.displayValue || (hasNotExpiredYet || realRec?.status === 'Active' ? 'Active' : 'Expired');
      const isActive = (realRec?.status === 'Active') || statusStr === 'Active' || item.status === 0 || hasNotExpiredYet;
      const hasActiveSession = Boolean(item.hasActiveSession || item.activeSession || realRec?.has_active_session || isActive);

      let fdtCode = realRec?.fdt_code || item.fdtCode || item.fdt_code;
      let fatCode = realRec?.fat_code || item.fatCode || item.fat_code;
      let zoneCode = realRec?.zone_code || portalZone;

      if (!fdtCode || !fatCode) {
        const parsed = parseFdtFatFromUsername(username, portalZone);
        if (!fdtCode) fdtCode = parsed.fdtCode;
        if (!fatCode) fatCode = parsed.fatCode;
        if (!zoneCode) zoneCode = parsed.zoneCode;
      }

      let powerState: 'up' | 'critical' | 'down' | 'not_scanned' = 'not_scanned';
      let rxPower = '--';

      const rawPwr = String(
        item.ontRxPower || 
        item.ont_rx_power || 
        item.opticalRxPower || 
        item.optical_rx_power || 
        ''
      ).trim();

      if (rawPwr && rawPwr !== 'undefined' && rawPwr !== 'null' && rawPwr !== '--') {
        rxPower = rawPwr.includes('dBm') || rawPwr === 'Offline' ? rawPwr : `${rawPwr} dBm`;
        const pVal = parseFloat(rxPower);
        if (!isNaN(pVal)) {
          powerState = pVal <= -27.0 ? 'critical' : 'up';
        } else if (rxPower === 'Offline') {
          powerState = 'down';
        } else {
          powerState = isActive ? 'up' : 'down';
        }
      } else {
        powerState = 'not_scanned';
        rxPower = '--';
      }

      const ontSerial = item.ontSerial || item.ont_serial || item.serialNumber || null;
      const ontVendor = (typeof item.ontVendor === 'object' ? item.ontVendor?.displayValue || item.ontVendor?.name : item.ontVendor) || item.ont_vendor || item.vendor || null;

      return {
        agent_name: (user as any)?.firstName ? `${(user as any).firstName} ${(user as any).lastName || ''}`.trim() : ((user as any)?.username || currentUserId),
        agent_id: currentUserId,
        subscription_id: String(subId),
        customer_id: item.customer?.id ? String(item.customer.id) : '',
        customer_name: customerName,
        zone_code: String(zoneCode),
        fdt_code: fdtCode,
        fat_code: fatCode,
        username: String(username),
        status: isActive ? 'Active' : 'Expired',
        expires_at: item.expires || item.expiresAt || '',
        optical_rx_power: rxPower,
        power_state: powerState,
        ont_serial: ontSerial,
        ont_vendor: ontVendor,
        has_active_session: hasActiveSession,
        updated_at: new Date().toISOString()
      };
    };

    const allSubs = uniquePortalItems.map(mapPortalItem);
    const allFats = buildFatGroups(allSubs, currentUserId);

    localPowerCache = {
      userId: currentUserId,
      fats: allFats,
      subscribers: allSubs,
      lastFetched: Date.now(),
      ontScanInProgress: false,
      totalCount: allSubs.length
    } as any;
    notifyListeners();

    console.log(`[Local Dev] Loaded complete ${allSubs.length} subscribers across ${allFats.length} FATs for ${currentUserId}.`);
    return { fats: allFats, subscribers: allSubs };
  } catch (err: any) {
    console.error('[Local Dev API] Failed to fetch live portal power data:', err);
    return { fats: [], subscribers: [] };
  }
};

export const api = {
  get: async (url: string, data?: any) => {
    let cleanUrl = url;
    const lowerUrl = cleanUrl.toLowerCase();

    if (lowerUrl.includes('/power/overview')) {
      console.log('[Local Dev API] Intercepted /power/overview. Fetching live portal data...');
      const liveData = await fetchLivePowerDataForLocalDev();
      return { data: { items: liveData.fats } };
    }

    if (lowerUrl.includes('/power/subscribers')) {
      console.log('[Local Dev API] Intercepted /power/subscribers. Fetching live portal data...');
      const liveData = await fetchLivePowerDataForLocalDev();
      return { data: { items: liveData.subscribers } };
    }

    if (lowerUrl.includes('/power/sync-status')) {
      return { data: { isSyncing: false, message: "المزامنة مكتملة" } };
    }
    
    if (lowerUrl.includes('/subscriber-sessions')) {
      console.log('[Mock API] Intercepted subscriber-sessions. Returning filtered mock network subscriber sessions.');
      
      // Parse query params from URL
      const urlObj = new URL(url, 'http://localhost');
      const params: Record<string, string> = {};
      urlObj.searchParams.forEach((v, k) => { params[k] = v; });

      const { user } = useAuthStore.getState();
      const activePartnerId = user?.partnerId ? String(user.partnerId) : '';
      const hasValidAccount = Boolean(activePartnerId || user?.username);

      if (!hasValidAccount) {
        return {
          data: {
            totalCount: 0,
            items: []
          } as any
        };
      }

      // Generate a pool of 1980 subscribers matching portal total
      const zones = ['FNS0028', 'FNS0029', 'FNS0030', 'FNS0031'];
      const names = [
        'محمد ضيول فرعون دويخ العساكره',
        'زين العابدين علي راضي موشي الحجامي',
        'عبداللطيف عبد جاسم قطن الموسوي',
        'سلوان توفيق علي سلوان اليدراوي',
        'حيدر علي حسين الموسوي',
        'أحمد جاسم كريم البصري',
        'مصطفى كمال الدين البغدادي',
        'كرار صادق جعفر النجفي',
        'علي شهيد سعدون الكعبي',
        'عمر فاروق فاضل الحمداني'
      ];

      const allItems: any[] = [];
      const totalPoolSize = 1980;
      const today = new Date('2026-07-29T12:00:00.000Z');

      for (let i = 0; i < totalPoolSize; i++) {
        const isOnline = i % 3 !== 2; // 1320 online (66.6%), 660 offline (33.3%)
        const zone = zones[i % zones.length];
        const customerName = names[i % names.length] + (i >= names.length ? ` (${i + 1})` : '');
        const username = i % 2 === 0 ? `39p7fd${i}` : `fa${(i % 4) + 28}fa${i}p${i % 3}`;

        // Create date spread: first 200 today, next 400 within next week, rest earlier
        const dateOffsetDays = i < 200 ? 0 : (i < 600 ? i % 7 : (i % 30));
        const itemDate = new Date(today.getTime() + (i < 600 ? 1 : -1) * dateOffsetDays * 86400000);

        allItems.push({
          subscription: { id: String(6958600 + i), displayValue: "FTTH_BASIC", isExpired: !isOnline && i % 2 === 0 },
          zone: { id: zone, displayValue: zone },
          partner: { id: "5922", displayValue: "علي شهيد سعدون فيصل" },
          status: { id: isOnline ? 0 : 1, entityType: "SessionStatus", displayValue: isOnline ? "Online" : "Offline" },
          customer: { id: String(2056000 + i), displayValue: customerName },
          username: username,
          ipAddress: isOnline ? `100.113.${(i % 100) + 10}.${(i % 200) + 1}` : null,
          macAddress: isOnline ? `04:27:58:bb:${(i % 80 + 10).toString(16)}:${(i % 80 + 10).toString(16)}` : null,
          totalUploadedBytes: isOnline ? 4212764444 + i * 1000000 : 0,
          totalDownloadedBytes: isOnline ? 94319929522 + i * 50000000 : 0,
          createdAt: isOnline ? itemDate.toISOString() : null,
          sessionTimeInSeconds: isOnline ? 3600 * ((i % 100) + 1) : null
        });
      }

      // Filter allItems across ENTIRE 1980 pool according to requested params
      let filtered = allItems;

      if (params.status !== undefined && params.status !== null && params.status !== '') {
        const sNum = Number(params.status);
        filtered = filtered.filter(item => item.status?.id === sNum);
      }

      if (params.username) {
        const q = String(params.username).toLowerCase().trim();
        filtered = filtered.filter(item =>
          (item.username || '').toLowerCase().includes(q) ||
          (item.customer?.displayValue || '').toLowerCase().includes(q) ||
          (item.subscription?.id || '').toLowerCase().includes(q) ||
          (item.partner?.displayValue || '').toLowerCase().includes(q) ||
          (item.ipAddress || '').toLowerCase().includes(q) ||
          (item.macAddress || '').toLowerCase().includes(q)
        );
      }

      if (params.zone && params.zone !== 'all') {
        filtered = filtered.filter(item => item.zone?.displayValue === params.zone || item.zone?.id === params.zone);
      }

      if (params.startDate) {
        const start = new Date(params.startDate);
        start.setHours(0, 0, 0, 0);
        filtered = filtered.filter(item => {
          if (!item.createdAt) return true; // Keep offline / timeless items
          return new Date(item.createdAt) >= start;
        });
      }

      if (params.endDate) {
        const end = new Date(params.endDate);
        end.setHours(23, 59, 59, 999);
        filtered = filtered.filter(item => {
          if (!item.createdAt) return true; // Keep offline / timeless items
          return new Date(item.createdAt) <= end;
        });
      }

      const totalCount = filtered.length;
      const pageSize = Number(params.pageSize) || 10;
      const pageNumber = Number(params.pageNumber) || 1;
      const startIndex = (pageNumber - 1) * pageSize;
      const items = filtered.slice(startIndex, startIndex + pageSize);

      return {
        data: {
          totalCount,
          items
        }
      };
    }

    if (lowerUrl.includes('/sessions') && !lowerUrl.includes('/subscriber-sessions')) {
      console.log('[Mock API] Intercepted sessions. Returning active sessions.');
      const currentUsername = useAuthStore.getState().user?.username || 'user';
      return { data: [
        {
          id: 'mock-session-id-12345',
          username: currentUsername,
          ipAddress: '127.0.0.1',
          clientInfo: 'Google Chrome',
          osName: 'Windows 11',
          lastAccess: Date.now(),
          location: 'بغداد، العراق'
        }
      ] };
    }
    
    if (
      lowerUrl.includes('/notifications/diagnostic-check') ||
      lowerUrl.includes('/notifications/subscription-debug') ||
      lowerUrl.includes('/notifications/user-logs') ||
      lowerUrl.includes('/notifications/admin-stats') ||
      lowerUrl.includes('/notifications/config') ||
      lowerUrl.includes('/notifications/run-cron-now') ||
      lowerUrl.includes('/integrations/telegram') ||
      lowerUrl.includes('/notifications/delivery-logs') ||
      lowerUrl.includes('/telegram-status')
    ) {
      try {
        const response = await backendClient.get(cleanUrl, { params: data });
        if (lowerUrl.includes('/notifications/user-logs') && (!Array.isArray(response.data) || response.data.length === 0)) {
          if (lowerUrl.includes('type=ticket')) {
            return { data: Array.isArray(response.data) ? response.data : [] };
          }
          generateExpiringSubscriberNotifications();
          return { data: [...mockNotificationLogs] };
        }
        if (lowerUrl.includes('/notifications/admin-stats') && (!response.data || typeof response.data.sentCount !== 'number')) {
          generateExpiringSubscriberNotifications();
          return {
            data: {
              sentCount: mockNotificationLogs.length,
              deviceCount: registeredDevicesSet.size || 1,
              successCount: mockNotificationLogs.filter(l => l.success !== false).length,
              failureCount: mockNotificationLogs.filter(l => l.success === false).length
            }
          };
        }
        return { data: response.data };
      } catch (err: any) {
        console.warn('[Mock API -> Backend] GET notification/telegram fallback:', err?.message || err);
        if (lowerUrl.includes('/notifications/user-logs')) {
          if (lowerUrl.includes('type=ticket')) {
            throw err;
          }
          generateExpiringSubscriberNotifications();
          return { data: [...mockNotificationLogs] };
        }
        if (lowerUrl.includes('/notifications/admin-stats')) {
          generateExpiringSubscriberNotifications();
          return {
            data: {
              sentCount: mockNotificationLogs.length,
              deviceCount: registeredDevicesSet.size || 1,
              successCount: mockNotificationLogs.filter(l => l.success !== false).length,
              failureCount: mockNotificationLogs.filter(l => l.success === false).length
            }
          };
        }
        if (lowerUrl.includes('/notifications/run-cron-now')) {
          generateExpiringSubscriberNotifications(true);
          return {
            data: {
              success: true,
              message: 'تم فحص التنبيهات واكتشاف الاشتراكات وتحديث السجلات بنجاح!'
            }
          };
        }
        if (lowerUrl.includes('/notifications/config')) {
          return {
            data: {
              onesignalAppId: '2dd6d0bd-f067-4ec6-b411-1c44c3b7337a'
            }
          };
        }
        if (lowerUrl.includes('/integrations/telegram/stats')) {
          return {
            data: {
              botStatus: 'connected',
              activeChannel: 'TELEGRAM',
              linkedCount: 12,
              sentToday: 5,
              failedToday: 0,
              skippedCount: 1,
              totalDispatched: 48
            }
          };
        }
        if (lowerUrl.includes('/telegram-status')) {
          return {
            data: {
              linked: true,
              telegramUsername: 'ahmed_ftth',
              telegramChatId: '987654321',
              telegramLinkedAt: new Date().toISOString(),
              notificationsEnabled: true
            }
          };
        }
        if (lowerUrl.includes('/notifications/delivery-logs')) {
          return {
            data: {
              items: [
                {
                  id: 'del-log-1',
                  subscriberId: 'SUB-10492',
                  customerName: 'حيدر علي ناصر',
                  channel: 'TELEGRAM',
                  notificationType: 'EXPIRY_3D',
                  expiryDate: '2026-09-01',
                  recipient: '987654321',
                  messageText: 'مرحباً حيدر علي ناصر 👋 نود تذكيرك بأن اشتراكك سينتهي بعد 3 أيام...',
                  status: 'SENT',
                  sentAt: Date.now() - 3600000,
                  createdAt: Date.now() - 3600000
                },
                {
                  id: 'del-log-2',
                  subscriberId: 'SUB-10493',
                  customerName: 'محمد جاسم كاظم',
                  channel: 'TELEGRAM',
                  notificationType: 'EXPIRY_1D',
                  expiryDate: '2026-08-29',
                  recipient: '123456789',
                  messageText: 'مرحباً محمد جاسم كاظم 👋 نود تذكيرك بأن اشتراكك سينتهي خلال 24 ساعة...',
                  status: 'SENT',
                  sentAt: Date.now() - 7200000,
                  createdAt: Date.now() - 7200000
                },
                {
                  id: 'del-log-3',
                  subscriberId: 'SUB-10494',
                  customerName: 'علي حسين شلش',
                  channel: 'TELEGRAM',
                  notificationType: 'EXPIRY_3D',
                  expiryDate: '2026-09-01',
                  recipient: 'Not Linked',
                  messageText: 'Subscription expiry notification pending Telegram linking',
                  status: 'SKIPPED',
                  errorCode: 'TELEGRAM_NOT_LINKED',
                  errorMessage: 'المشترك لم يقم بربط حسابه على تليكرام حتى الآن',
                  createdAt: Date.now() - 10800000
                }
              ]
            }
          };
        }
        return { data: { success: false, error: err?.message || err } };
      }
    }

    if (lowerUrl.includes('/notifications/settings')) {
      try {
        const response = await backendClient.get(cleanUrl, { params: data });
        return { data: response.data };
      } catch (e) {
        return {
          data: {
            email_enabled: false,
            email: '',
            notification_channel: 'TELEGRAM',
            whatsapp_enabled: false,
            telegram_enabled: true,
            telegram_bot_token: '719283****98aB',
            telegram_bot_username: 'FTTHNexusAlertsBot',
            telegram_bot_name: 'FTTH Nexus Alert Bot',
            has_telegram_token: true,
            telegram_templates: {
              renewal: "مرحباً {{subscriber_name}} 👋\n\nتم تجديد اشتراك الإنترنت الخاص بك بنجاح في باقة {{plan_name}}.\n\nشكراً لاختيارك خدماتنا 🌐",
              looming: "مرحباً {{subscriber_name}} 👋\n\nنود تذكيرك بأن اشتراك الإنترنت الخاص بك في باقة {{plan_name}} سينتهي خلال 24 ساعة:\n\n📅 تاريخ الانتهاء: {{expiry_date}}\n⏳ المتبقي: {{days_remaining}} يوم\n\nيرجى التجديد لتجنب انقطاع الخدمة.",
              expired: "تنبيه هام للمشترك {{subscriber_name}} ⚠️\n\nلقد انتهى اشتراك الإنترنت الخاص بك في باقة {{plan_name}} بتاريخ {{expiry_date}}.\n\nيرجى تسديد الاشتراك لإعادة تفعيل الخدمة فوراً.",
              days3: "مرحباً {{subscriber_name}} 👋\n\nنود تذكيرك بأن اشتراك الإنترنت الخاص بك في باقة {{plan_name}} سينتهي بعد 3 أيام:\n\n📅 تاريخ الانتهاء: {{expiry_date}}\n⏳ المتبقي: {{days_remaining}} أيام\n\nيرجى التجديد لتفادي انقطاع الخدمة."
            },
            telegram_toggles: {
              start: true,
              days3: true,
              hours24: true,
              expiredAuto: true
            }
          }
        };
      }
    }
    
    if (lowerUrl.includes('/notifications/vapid-public-key') || lowerUrl.includes('/push/vapid-key')) {
      return { data: { publicKey: 'BO_L_p7apDkwCF2NJ_vnz6qCoVsDdhyEyZg5MTnKSfRGMqeXyZTOHYte06Dw59ZoqOQ7NHdTkh26lruzRcDfbxI' } };
    }
    
    if (lowerUrl.includes('/notifications/push/status')) {
      return { data: { enabled: true } };
    }

    if (lowerUrl.includes('/network-elements/fdts')) {
      const urlObj = new URL(url, 'http://localhost');
      const zoneId = urlObj.searchParams.get('zoneId') || (data?.zoneId) || '';
      try {
        const res = await localClient.get(cleanUrl, { params: data }).catch(() => null);
        if (res && res.data && (res.data.model?.fdts || res.data.model || res.data.items || Array.isArray(res.data))) {
          return { data: res.data };
        }
      } catch (e) {}

      const zoneFdtsMap: Record<string, string[]> = {
        'FNS0026': ['FNS26', 'FDT26'],
        'FNS0028': ['FNS28', 'FDT28'],
        'FNS0029': ['FNS29', 'FDT29'],
        'FNS0030': ['FNS30', 'FDT30'],
        'FNS0031': ['FNS31', 'FDT31']
      };
      const codeList = zoneFdtsMap[zoneId] || [zoneId.replace('FNS00', 'FNS').replace('FNS0', 'FNS')];
      const items = codeList.map(code => ({
        id: code,
        code: code,
        displayValue: code,
        name: code,
        zoneId: zoneId
      }));
      return { data: { model: { fdts: items }, items } };
    }

    const params = { ...data };
    
    try {
      const response = await localClient.get(cleanUrl, { params });
      return { data: response.data };
    } catch (err: any) {
      // Direct fallbacks in mock client to prevent uncaught exceptions in console
      if (lowerUrl.includes('partners/dashboard/summary')) {
        return {
          data: {
            model: {
              customers: { totalCount: 2045 },
              subscriptions: {
                totalCount: 2212,
                totalActive: 1403,
                totalExpiring: 333,
                totalExpired: 476,
                totalTrial: 0,
                totalOnline: 1361,
                totalOffline: 601
              }
            }
          }
        };
      }
      throw err;
    }
  },
  post: async (url: string, data?: any) => {
    const body = { ...data };
    
    const lowerUrl = url.toLowerCase();
    
    // Intercept custom backend register/subscribe endpoints
    if (lowerUrl.includes('/power/fat/sync') || lowerUrl.includes('/sync/power') || lowerUrl.includes('/sync/customer-details')) {
      console.log('[Local Dev API] Intercepted power sync trigger. Invalidating cache and forwarding sync to backend...');
      invalidateLocalPowerCache();
      try {
        const response = await backendClient.post(url, body);
        return { data: response.data?.success !== undefined ? response.data : { success: true, message: 'بدأت عملية المزامنة بنجاح' } };
      } catch (err: any) {
        console.warn('[Local Dev API] Backend sync trigger notice:', err?.message || err);
        return { data: { success: true, message: 'بدأت مزامنة تفاصيل الكابينات والخطوط (FDT/FAT) في الخلفية بنجاح.' } };
      }
    }

    // Telegram and Notification settings endpoints forwarding
    if (
      lowerUrl.includes('/notifications/trigger-ticket-push') ||
      lowerUrl.includes('/notifications/trigger') ||
      lowerUrl.includes('/notifications/register-device') ||
      lowerUrl.includes('/notifications/unsubscribe') ||
      lowerUrl.includes('/notifications/test-push-direct') ||
      lowerUrl.includes('/notifications/push/test') ||
      lowerUrl.includes('/notifications/send-email') ||
      lowerUrl.includes('/notifications/settings') ||
      lowerUrl.includes('/notifications/cleanup-stale') ||
      lowerUrl.includes('/integrations/telegram') ||
      lowerUrl.includes('/telegram-link') ||
      lowerUrl.includes('/telegram-unlink')
    ) {
      try {
        const response = await backendClient.post(url, body);
        return { data: response.data };
      } catch (err: any) {
        console.warn('[Mock API -> Backend] POST notification/telegram notice:', err?.message || err);
        if (lowerUrl.includes('/notifications/trigger-ticket-push')) {
          return { data: { success: true, dispatchedCount: 1, message: 'Dispatched via fallback' } };
        }
        if (lowerUrl.includes('/notifications/trigger')) {
          generateExpiringSubscriberNotifications();
          const title = body?.title || '🔔 إشعار فوري تجريبي';
          const message = body?.message || body?.body || 'تم استقبال هذا الإشعار بنجاح في الخلفية. 🎉';
          const newNotif = {
            id: `notif-test-${Date.now()}`,
            title,
            body: message,
            type: body?.type || 'GENERAL',
            status: 'UNREAD',
            createdAt: Date.now(),
            success: true
          };
          mockNotificationLogs.unshift(newNotif);
          return { data: { success: true, notification: newNotif } };
        }
        if (lowerUrl.includes('/integrations/telegram/test-connection')) {
          return {
            data: {
              success: true,
              message: 'تم التحقق من اتصال البوت بنجاح',
              botId: 123456789,
              botUsername: 'FTTHNexusAlertsBot',
              botName: 'FTTH Nexus Alert Bot'
            }
          };
        }
        if (lowerUrl.includes('/integrations/telegram/test-message')) {
          return { data: { success: true, message: 'تم إرسال رسالة الاختبار بنجاح عبر Telegram' } };
        }
        if (lowerUrl.includes('/telegram-link')) {
          const token = Math.random().toString(36).substring(2, 10);
          return {
            data: {
              success: true,
              link: `https://t.me/FTTHNexusAlertsBot?start=${token}`,
              token,
              botUsername: 'FTTHNexusAlertsBot',
              expiresAt: Date.now() + 86400000
            }
          };
        }
        if (lowerUrl.includes('/telegram-unlink')) {
          return { data: { success: true, message: 'تم إلغاء ربط حساب Telegram بنجاح' } };
        }
        return { data: { success: true, message: 'تم حفظ الإعدادات بنجاح' } };
      }
    }
    if (lowerUrl.includes('/notifications/unsubscribe')) {
      return { data: { success: true } };
    }
    if (lowerUrl.includes('/notifications/mark-read')) {
      try {
        const response = await backendClient.post(url, body);
        if (body?.all) {
          if (body?.delete) {
            mockNotificationLogs.length = 0;
          } else {
            mockNotificationLogs.forEach(n => n.status = 'READ');
          }
        } else if (body?.notificationId) {
          const target = mockNotificationLogs.find(n => n.id === body.notificationId);
          if (target) {
            if (body?.delete) {
              const idx = mockNotificationLogs.findIndex(n => n.id === body.notificationId);
              if (idx !== -1) mockNotificationLogs.splice(idx, 1);
            } else {
              target.status = 'READ';
            }
          }
        }
        return { data: response.data || { success: true } };
      } catch (err: any) {
        console.warn('[Mock API -> Backend] POST /notifications/mark-read failed:', err?.message || err);
        throw err;
      }
    }
    
    // If it's a token request, serialize as urlencoded to prevent 415 Unsupported Media Type
    if (url.includes('token')) {
      const params = new URLSearchParams();
      for (const [key, val] of Object.entries(body)) {
        params.append(key, String(val));
      }
      const response = await localClient.post(url, params.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' }
      });
      return { data: response.data };
    }

    const response = await localClient.post(url, body);
    return { data: response.data };
  },
  put: async (url: string, data?: any) => {
    const body = { ...data };
    delete body.ftth_token;
    try {
      const response = await localClient.put(url, body);
      return { data: response.data };
    } catch (err: any) {
      if (url.includes('/device') || url.includes('/subscriptions/')) {
        console.warn(`[MockClient] PUT ${url} intercepted (${err?.response?.status || err.message}). Fallback to success response.`);
        return { data: { success: true, model: body } };
      }
      throw err;
    }
  },
  delete: async (url: string) => {
    const lowerUrl = url.toLowerCase();
    if (lowerUrl.includes('/telegram-link') || lowerUrl.includes('/integrations/telegram')) {
      try {
        const response = await backendClient.delete(url);
        return { data: response.data };
      } catch (e) {
        return { data: { success: true, message: 'تم الحذف بنجاح' } };
      }
    }
    const response = await localClient.delete(url);
    return { data: response.data };
  }
};
